import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useStore } from '@tanstack/react-store'
import { ChevronLeft, CreditCard, Globe, LogOut, Menu, Settings, X } from 'lucide-react'
import { authStore, isTeacherRole } from '@/store/authStore.ts'
import { ApiClient } from '@/lib/api/client.ts'
import { authActions } from '@/store/authStore.ts'
import { Avatar, AvatarFallback } from '@/components/ui/avatar.tsx'
import { Separator } from '@/components/ui/separator.tsx'
import { cn } from '@/lib/utils.ts'

export function Sidebar() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const user = useStore(authStore, (state) => state.user)
  const userRole = useStore(authStore, (state) => state.userRole)

  const [collapsed, setCollapsed] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  const currentLanguage = i18n.language

  const toggleLanguage = () => {
    const newLang = currentLanguage === 'de' ? 'en' : 'de'
    i18n.changeLanguage(newLang)
  }

  const firstName = (user?.user_metadata?.first_name as string) || ''
  const lastName = (user?.user_metadata?.last_name as string) || ''
  const email = user?.email || ''
  const fullName = [firstName, lastName].filter(Boolean).join(' ') || email
  const initials = [firstName, lastName]
    .filter(Boolean)
    .map((n) => n.charAt(0).toUpperCase())
    .join('') || email?.charAt(0).toUpperCase() || 'U'

  const handleSignOut = async () => {
    setMobileOpen(false)
    await ApiClient.signOut()
    authActions.clearAuth()
    navigate('/auth')
  }

  const handleNavigate = (path: string) => {
    setMobileOpen(false)
    navigate(path)
  }

  const isActive = (path: string) => location.pathname === path

  const isTeacher = isTeacherRole(userRole)

  // The teacher portal lives in the admin app; teachers only have their
  // settings here. Everyone else sees the regular navigation.
  const navLinks = isTeacher
    ? [{ path: '/settings', label: t('settings.title'), icon: Settings }]
    : [
        { path: '/subscriptions/manage', label: t('subscriptions.mySubscriptions'), icon: CreditCard },
        { path: '/settings', label: t('settings.title', { defaultValue: 'Settings' }), icon: Settings },
      ]

  const homePath = isTeacher ? '/settings' : '/subscriptions/manage'

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center justify-between px-4 py-5">
        <button onClick={() => handleNavigate(homePath)} className="flex-shrink-0">
          {collapsed ? (
            <img src="/logo_icon.png" alt="GM" className="h-8 w-8" />
          ) : (
            <img
              src="/GM_Brand_2025_Logo - Horizontal.png"
              alt="German Method"
              className="h-10"
            />
          )}
        </button>
        {/* Collapse button — desktop only */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden md:flex items-center justify-center h-7 w-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          <ChevronLeft className={cn('h-4 w-4 transition-transform', collapsed && 'rotate-180')} />
        </button>
      </div>

      {/* Navigation */}
      <div className="px-3 py-2">
        <p className={cn(
          'text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2',
          collapsed ? 'px-0 text-center' : 'px-2'
        )}>
          {collapsed ? '' : t('common.navigation', { defaultValue: 'Navigation' })}
        </p>
        <nav className="flex flex-col gap-1">
          {navLinks.map((link) => (
            <button
              key={link.path}
              onClick={() => handleNavigate(link.path)}
              className={cn(
                'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                isActive(link.path)
                  ? 'bg-[#26247B] text-white'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted',
                collapsed && 'justify-center px-2'
              )}
              title={collapsed ? link.label : undefined}
            >
              <link.icon className="h-5 w-5 flex-shrink-0" />
              {!collapsed && <span>{link.label}</span>}
            </button>
          ))}
        </nav>
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Bottom section */}
      <div className="px-3 py-2">
        <Separator className="mb-3" />

        {/* Language toggle */}
        <button
          onClick={toggleLanguage}
          className={cn(
            'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors w-full',
            collapsed && 'justify-center px-2'
          )}
          title={collapsed ? (currentLanguage === 'de' ? 'English' : 'Deutsch') : undefined}
        >
          <Globe className="h-5 w-5 flex-shrink-0" />
          {!collapsed && <span>{currentLanguage === 'de' ? 'English' : 'Deutsch'}</span>}
        </button>

        {/* Sign out */}
        <button
          onClick={handleSignOut}
          className={cn(
            'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-red-500 hover:text-red-600 hover:bg-red-50 transition-colors w-full',
            collapsed && 'justify-center px-2'
          )}
          title={collapsed ? t('auth.signOut') : undefined}
        >
          <LogOut className="h-5 w-5 flex-shrink-0" />
          {!collapsed && <span>{t('auth.signOut')}</span>}
        </button>

        <Separator className="my-3" />

        {/* User profile */}
        <div className={cn(
          'flex items-center gap-3 px-3 py-2',
          collapsed && 'justify-center px-0'
        )}>
          <Avatar className="h-9 w-9 flex-shrink-0">
            <AvatarFallback className="bg-[#26247B] text-white text-sm font-medium">
              {initials}
            </AvatarFallback>
          </Avatar>
          {!collapsed && (
            <div className="flex flex-col overflow-hidden">
              <p className="text-sm font-medium truncate">{fullName}</p>
              <p className="text-xs text-muted-foreground truncate">{email}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )

  return (
    <>
      {/* Mobile top bar — sits below the AppDownloadBanner (top-12 = 3rem = banner height). */}
      <div className="md:hidden fixed top-12 left-0 right-0 z-30 flex items-center h-14 px-4 bg-white border-b border-border">
        <button
          onClick={() => setMobileOpen(true)}
          className="h-10 w-10 flex items-center justify-center rounded-lg hover:bg-muted -ml-2"
        >
          <Menu className="h-5 w-5" />
        </button>
        <button onClick={() => navigate(homePath)} className="ml-2">
          <img
            src="/GM_Brand_2025_Logo - Horizontal.png"
            alt="German Method"
            className="h-8"
          />
        </button>
      </div>

      {/* Mobile spacer — pushes content below the fixed top bar */}
      <div className="md:hidden h-14 flex-shrink-0" />

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar — mobile drawer is fixed h-screen; desktop sits in flex flow with h-full so it
          respects the AppDownloadBanner's offset above the AppLayout container. */}
      <aside
        className={cn(
          'fixed md:relative z-50 md:z-auto h-screen md:h-full bg-background border-r border-border flex-shrink-0 transition-all duration-300',
          'md:translate-x-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
          collapsed ? 'w-[68px]' : 'w-64'
        )}
      >
        {/* Mobile close button */}
        {mobileOpen && (
          <button
            onClick={() => setMobileOpen(false)}
            className="md:hidden absolute top-4 right-4 h-8 w-8 flex items-center justify-center rounded-md hover:bg-muted z-10"
          >
            <X className="h-5 w-5" />
          </button>
        )}
        {sidebarContent}
      </aside>
    </>
  )
}
